import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

/**
 * Keys that may be overridden at runtime via window.__ARCADE_DASHBOARD_CONFIG__.
 * Anything outside this list stays build-time only. Frozen so app code can't
 * mutate the allowlist to smuggle in extra overrides.
 */
const RUNTIME_OVERRIDABLE_KEYS = Object.freeze([
  "VITE_EXPERIENCE_API_URL",
  "VITE_ACCOUNT_UI_URL",
] as const);

type RuntimeOverridableKey = (typeof RUNTIME_OVERRIDABLE_KEYS)[number];
type RuntimeOverrides = Readonly<
  Partial<Record<RuntimeOverridableKey, string>>
>;

// Window augmentation is defined in the consuming app (apps/dashboard/src/vite-env.d.ts),
// but this lib is type-checked in isolation — so we read the global through a local
// cast rather than relying on the consumer's ambient types.
type RuntimeConfigGlobal = {
  __ARCADE_DASHBOARD_CONFIG__?: Readonly<Record<string, unknown>>;
};

function readRuntimeOverrides(): RuntimeOverrides {
  if (typeof window === "undefined") {
    return Object.freeze({});
  }

  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const raw = (window as unknown as RuntimeConfigGlobal)
    .__ARCADE_DASHBOARD_CONFIG__;
  if (!raw || typeof raw !== "object") {
    return Object.freeze({});
  }

  const overrides: Partial<Record<RuntimeOverridableKey, string>> = {};
  for (const key of RUNTIME_OVERRIDABLE_KEYS) {
    const value = raw[key];
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      overrides[key] = trimmed;
    }
  }
  return Object.freeze(overrides);
}

const runtimeOverrides = readRuntimeOverrides();

// Merge order: runtime wins over build-time. Build-time Vite values remain
// the baseline so local dev and SaaS deploys behave exactly as before.
export const env = createEnv({
  clientPrefix: "VITE_",
  client: {
    VITE_ACCOUNT_UI_URL: z.url().default("https://account.bosslevel.dev"),
    VITE_EXPERIENCE_API_URL: z
      .url()
      .default("https://experience.bosslevel.dev"),
    VITE_DEBUG: z
      .enum(["true", "false"])
      .optional()
      .transform((v) => v === "true"),
    VITE_ARCADE_ENVIRONMENT: z.string().default("local"),
    VITE_SENTRY_DSN: z.string().optional(),
    VITE_SENTRY_PROJECT: z.string().optional(),
    VITE_SENTRY_TRACES_SAMPLE_RATE: z.string().optional(),
    VITE_PUBLIC_POSTHOG_KEY: z.string().optional(),
    VITE_STRIPE_PK: z.string().optional(),
    VITE_PYLON_APP_ID: z.string().optional(),
  },
  runtimeEnv: { ...import.meta.env, ...runtimeOverrides },
  emptyStringAsUndefined: true,
});
